import React, { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import { readSignupDraft, saveSignupDraft, clearSignupDraft } from '../../utils/signupDraft';
import { normaliseUrl } from '../../utils/normaliseUrl';
import { EMAIL_PROVIDER_MESSAGE, isEmailProviderHost } from '../../utils/businessWebsite';
import CodeHelp from './CodeHelp';
import SetupHelp from './SetupHelp';
import TurnstileWidget, { turnstileEnabled } from './TurnstileWidget';
import {
  signupAttribution,
  trackSignupCodeRequested,
  trackSignupConversion,
} from '../../utils/googleAnalytics';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Order matches the form, so the first problem found is the first one seen.
const FIELD_INPUTS = {
  businessName: 'signup-business-name',
  website: 'signup-website',
  description: 'signup-description',
  email: 'signup-email',
  terms: 'signup-terms',
};

const inputClass = (invalid) => `mt-2 block w-full rounded-lg border px-3.5 py-3 text-gray-900 shadow-sm focus:outline-none focus:ring-2 sm:text-sm ${
  invalid ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
}`;

const FieldError = ({ id, message }) => (message
  ? <p id={id} role="alert" className="mt-1.5 text-sm font-medium text-red-700">{message}</p>
  : null);

const SignupCodeForm = ({ onSignupSuccess, onBack, initialEmail = '' }) => {
  const [draft] = useState(readSignupDraft);
  const [step, setStep] = useState('business');
  const [businessName, setBusinessName] = useState(draft.businessName || '');
  const [website, setWebsite] = useState(draft.website || '');
  const [noWebsite, setNoWebsite] = useState(Boolean(draft.noWebsite));
  const [description, setDescription] = useState(draft.description || '');
  useEffect(() => {
    saveSignupDraft({ businessName, website, noWebsite, description });
  }, [businessName, website, noWebsite, description]);
  const [email, setEmail] = useState(initialEmail);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  // Positive confirmation, separate from error. Without it, "Resend code" on
  // the code step changed nothing visible whether it sent or not -- the server
  // suppresses a resend inside its own 60s window and still answers 200 -- so
  // people concluded the button was broken and left with a working code in
  // their spam folder.
  const [notice, setNotice] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Mirrors the server's resend cooldown so the wait is shown, not guessed at.
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [now, setNow] = useState(Date.now());
  // A field shows its problem once it has been left, or after a submit.
  const [touched, setTouched] = useState({});
  // Cloudflare Turnstile token: single-use, so reset after every request. Held
  // in a ref, not state, because requestCode may have to wait for a fresh one
  // mid-call and a state value captured at render would be stale by then.
  const botTokenRef = useRef('');
  const turnstileRef = useRef(null);
  const rememberToken = (token) => { botTokenRef.current = token || ''; };
  const cooldownLeft = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));

  useEffect(() => {
    if (cooldownUntil <= Date.now()) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(timer);
  }, [cooldownUntil]);

  // Every request resets the widget, so the next one starts with no token.
  // Turnstile re-issues within a second or two in interaction-only mode, but
  // the old code failed instantly and told people to tick a checkbox rendered
  // below a tall help panel -- off-screen on a phone. Wait for it instead.
  const waitForToken = async () => {
    for (let attempt = 0; attempt < 25; attempt += 1) {
      if (botTokenRef.current) return botTokenRef.current;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => { setTimeout(resolve, 200); });
    }
    return '';
  };
  const touch = (field) => () => setTouched((current) => ({ ...current, [field]: true }));
  const requestSignupCode = useAuthStore((state) => state.requestSignupCode);
  const completeSignupWithCode = useAuthStore((state) => state.completeSignupWithCode);

  const websiteUrl = noWebsite ? '' : normaliseUrl(website);
  const websiteProblem = (() => {
    if (noWebsite) return '';
    if (!website.trim()) return 'Enter your business website address.';
    try {
      const { hostname } = new URL(websiteUrl);
      if (!hostname.includes('.') || /\s/.test(website.trim())) throw new Error('no host');
      // A required field invites the address someone signs in with. Crawling
      // gmail.com left a workspace with nothing to answer from.
      if (isEmailProviderHost(hostname)) return EMAIL_PROVIDER_MESSAGE;
    } catch {
      return 'Enter your business website address, for example yourbusiness.com';
    }
    return '';
  })();

  // The button used to be greyed out while the phone was invalid, with the
  // reason only in a tooltip: people filled every field, clicked, and nothing
  // happened. It now always works and says what to fix, next to the field.
  const problems = {
    businessName: businessName.trim() ? '' : 'Enter your business name.',
    website: websiteProblem,
    description: noWebsite && description.trim().length < 80 ? 'Add at least 80 characters about your services, hours and contact details.' : '',
    email: !email.trim()
      ? 'Enter your work email.'
      : EMAIL_PATTERN.test(email.trim()) ? '' : 'Enter an email address like you@company.com.',
    terms: termsAccepted ? '' : 'Review and accept the Terms and Privacy Policy to continue.',
  };
  const shown = (field) => (touched[field] ? problems[field] : '');

  const validate = (fields) => {
    const firstProblem = fields.find((field) => problems[field]);
    if (firstProblem) {
      setTouched((current) => ({ ...current, ...Object.fromEntries(fields.map((field) => [field, true])) }));
      setError('');
      const target = document.getElementById(FIELD_INPUTS[firstProblem]);
      target?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      target?.focus?.({ preventScroll: true });
      return false;
    }
    return true;
  };
  const continueBusiness = (event) => {
    event.preventDefault();
    if (validate(['businessName', 'website', 'description'])) { setError(''); setStep('details'); }
  };
  const requestCode = async (event) => {
    event.preventDefault();
    if (isLoading || !validate(Object.keys(FIELD_INPUTS))) return;
    setError('');
    setNotice('');
    setIsLoading(true);
    try {
      let token = botTokenRef.current;
      // Still no token (a blocked or broken widget): send anyway and let the
      // API decide. It requires one only once its secret is set, so the widget
      // can never block signup on its own.
      if (turnstileEnabled() && !token) token = await waitForToken();
      if (token) await requestSignupCode(email, token);
      else await requestSignupCode(email);
      trackSignupCodeRequested();
      // Say so out loud. The server answers the same 200 whether it sent a
      // code or suppressed a too-early resend, so the countdown below is what
      // keeps the two from looking like a dead button.
      setNotice(step === 'code'
        ? `New code sent to ${email}. It can take a minute — check spam and promotions.`
        : '');
      setCooldownUntil(Date.now() + 60000);
      setStep('code');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not send the code. Please try again.');
    } finally {
      setIsLoading(false);
      turnstileRef.current?.reset();
    }
  };

  const verifyCode = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');
    setIsLoading(true);
    try {
      const result = await completeSignupWithCode({
        email,
        code,
        business_name: businessName,
        website: websiteUrl,
        business_description: noWebsite ? description.trim() : '',
        terms_accepted: termsAccepted,
        attribution: signupAttribution(),
      });
      // This is the conversion boundary: the backend has consumed the verified
      // code and created both the account and workspace. Merely requesting a
      // code or opening the form is not reported as a Google Ads conversion.
      trackSignupConversion(result?.user?.tenant_id, email);
      clearSignupDraft();
      onSignupSuccess(result);
    } catch (err) {
      // The API now names which of expired / locked out / wrong digits it was,
      // and each of those messages ends in the action to take.
      setError(err.response?.data?.detail || 'That code did not work. Use Resend code and we will email you a new one.');
      if (err.response?.status === 409) {
        // The code was valid and therefore consumed, but account/workspace
        // creation could not continue (usually an existing email/business).
        // Return to details so the user does not keep retrying a spent code.
        setCode('');
        setStep('details');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-7">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-950">Create an AI agent for your business</h2>
        <p className="mt-2 text-sm text-gray-600">Start free. 30-day Growth trial, no card.</p>
        <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-blue-700">
          Step {step === 'business' ? '1' : '2'} of 3 · {step === 'business' ? 'Your business' : 'Verify your email'}
        </p>
        <p className="mt-2 text-sm leading-6 text-gray-600">
          {step !== 'code' ? (
            <>
              {step === 'business'
                ? 'Add your website or a short description. No website needed.'
                : 'Save your progress with a one-time code. No password needed.'}
            </>
          ) : (
            <>
              We emailed <strong className="font-semibold text-gray-800">{email}</strong>. A new address gets its 6-digit code;
              {' '}if this address already has an account, the email explains how to sign in instead.
            </>
          )}
        </p>
        {step !== 'code' && (
          // Step 3 happens after sign-in; without the old side panel nothing
          // else on the page says what it is.
          <p className="mt-1 text-sm leading-6 text-gray-500">Next: review your business information and test your agent.</p>
        )}
      </div>

      {step === 'business' && (
        <form className="space-y-5" onSubmit={continueBusiness} noValidate>
          <div>
            <label htmlFor="signup-business-name" className="block text-sm font-medium text-gray-700">Business name</label>
            <input id="signup-business-name" name="organization" type="text" autoComplete="organization" required maxLength={100} value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              onBlur={touch('businessName')}
              aria-invalid={Boolean(shown('businessName')) || undefined}
              aria-describedby={shown('businessName') ? 'signup-business-name-error' : undefined}
              className={inputClass(shown('businessName'))}
              placeholder="Your business" />
            <FieldError id="signup-business-name-error" message={shown('businessName')} />
          </div>

          <label className="flex min-h-[44px] items-center gap-3 text-sm text-gray-700">
            <input type="checkbox" checked={noWebsite} onChange={(e) => setNoWebsite(e.target.checked)} />
            I don’t have a website
          </label>
          {!noWebsite && (<>          <div>
            <label htmlFor="signup-website" className="block text-sm font-medium text-gray-700">Business website</label>
            <input id="signup-website" name="url" type="text" inputMode="url" autoComplete="url" required maxLength={500} value={website}
              onChange={(e) => setWebsite(e.target.value)}
              onBlur={() => { setWebsite((current) => current.trim()); touch('website')(); }}
              aria-invalid={Boolean(shown('website')) || undefined}
              aria-describedby={shown('website') ? 'signup-website-error signup-website-hint' : 'signup-website-hint'}
              className={inputClass(shown('website'))}
              placeholder="yourbusiness.com" />
            <FieldError id="signup-website-error" message={shown('website')} />
            <p id="signup-website-hint" className="mt-1.5 text-xs leading-5 text-gray-600">
              We’ll read your website, then show you the business information to check before creating your agent.
            </p>
          </div>
</>)}
          {noWebsite && (
            <div>
              <label htmlFor="signup-description" className="block text-sm font-medium text-gray-700">Tell us about your business</label>
              <textarea id="signup-description" rows={5} maxLength={12000} value={description}
                onChange={(e) => setDescription(e.target.value)} onBlur={touch('description')}
                aria-invalid={Boolean(shown('description')) || undefined}
                aria-describedby="signup-description-hint signup-description-error"
                className={inputClass(shown('description'))}
                placeholder="What do you offer? Add your services, opening hours, location and how customers can contact you." />
              <p id="signup-description-hint" className="mt-2 text-sm text-gray-600">A short paragraph is enough to start. Only include facts you want customers to know. You can add more later.</p>
              <FieldError id="signup-description-error" message={shown('description')} />
            </div>
          )}
          <button type="submit" className="min-h-[48px] w-full rounded-lg bg-blue-600 px-4 py-3 font-semibold text-white hover:bg-blue-700">Continue</button>
          <button type="button" onClick={() => onBack(email.trim())} className="min-h-[44px] w-full text-sm text-blue-700">Already have an account? Sign in</button>
        </form>
      )}
      {step === 'details' && (
        <form className="space-y-5" onSubmit={requestCode} noValidate>
          <p className="text-sm text-gray-600">We’ll save progress for <strong>{businessName}</strong>.</p>
          <button type="button" onClick={() => setStep('business')} className="min-h-[44px] text-sm font-medium text-blue-700">Edit business details</button>
          <div>
            <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700">Work email</label>
            <input id="signup-email" name="email" type="email" required maxLength={100} autoComplete="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={touch('email')}
              aria-invalid={Boolean(shown('email')) || undefined}
              aria-describedby={shown('email') ? 'signup-email-error' : undefined}
              className={inputClass(shown('email'))}
              placeholder="you@company.com" />
            <FieldError id="signup-email-error" message={shown('email')} />
          </div>
          <label className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3.5 py-3 text-sm leading-5 text-gray-700 ${
            shown('terms') ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'
          }`}>
            <input
              id="signup-terms"
              type="checkbox"
              required
              checked={termsAccepted}
              onChange={(event) => { setTermsAccepted(event.target.checked); touch('terms')(); }}
              aria-invalid={Boolean(shown('terms')) || undefined}
              aria-describedby={shown('terms') ? 'signup-terms-error' : undefined}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span>
              Serviti uses AI. By continuing, you agree to SERVITI AI (OPC) PVT LTD&apos;s{' '}
              <a
                href="https://serviti.ai/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-900"
              >
                Terms
              </a>
              {' '}and{' '}
              <a
                href="https://serviti.ai/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-900"
              >
                Privacy Policy
              </a>
              , including the <strong>AI Agent and Customer Data Terms</strong>. Chats may be reviewed to support and improve your AI agent.{' '}
              <a
                href="https://serviti.ai/terms#ai-agent-data-terms"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-900"
              >
                Learn more
              </a>
              .
            </span>
          </label>
          <FieldError id="signup-terms-error" message={shown('terms')} />
          {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
          <TurnstileWidget ref={turnstileRef} action="signup" onToken={rememberToken} />
          <button type="submit" disabled={isLoading}
            className="flex min-h-[46px] w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70">
            {isLoading ? (
              <span className="inline-flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                Sending code...
              </span>
            ) : 'Send verification code and continue'}
          </button>
          <button type="button" onClick={() => onBack(email.trim())} className="min-h-[44px] w-full text-center text-sm font-medium text-blue-600 hover:text-blue-500">
            Back to sign in
          </button>
        </form>
      )}

      {step === 'code' && (
        <form className="space-y-5" onSubmit={verifyCode}>
          <div>
            <label htmlFor="signup-code" className="block text-sm font-medium text-gray-700">6-digit code</label>
            <input id="signup-code" type="text" inputMode="numeric" autoComplete="one-time-code" required maxLength={6}
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="mt-2 block w-full rounded-lg border border-gray-300 px-3.5 py-3 text-center text-lg tracking-[0.5em] text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="000000" />
          </div>
          {notice && <div role="status" className="rounded-lg border border-green-300 bg-green-50 p-3 text-sm font-medium text-green-900">{notice}</div>}
          {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
          {/* Above the buttons, not below the help panel: when Turnstile does
              show a checkbox it has to be where the person is already looking. */}
          <TurnstileWidget ref={turnstileRef} action="signup" onToken={rememberToken} />
          <button type="submit" disabled={isLoading || code.length !== 6}
            className="flex min-h-[46px] w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70">
            {isLoading ? 'Creating workspace...' : 'Verify and create workspace'}
          </button>
          <button
            type="button"
            onClick={() => onBack(email.trim())}
            className="flex min-h-[46px] w-full items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-800 shadow-sm hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
          >
            Already have an account? Sign in
          </button>
          <div className="flex flex-col gap-1 text-sm min-[360px]:flex-row min-[360px]:items-center min-[360px]:justify-between">
            <button type="button" onClick={() => { setStep('details'); setCode(''); setError(''); }} className="min-h-[44px] px-2 text-left font-medium text-blue-600 hover:text-blue-500 min-[360px]:text-center">Change details</button>
            <button type="button" onClick={requestCode} disabled={isLoading || cooldownLeft > 0} className="inline-flex min-h-[44px] items-center gap-1.5 px-2 font-medium text-blue-600 hover:text-blue-500 disabled:opacity-50">
              {isLoading && <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />}
              {isLoading ? 'Sending...' : (cooldownLeft > 0 ? `Resend code in ${cooldownLeft}s` : 'Resend code')}
            </button>
          </div>
          <CodeHelp />
        </form>
      )}
      <SetupHelp />
    </div>
  );
};

export default SignupCodeForm;
